import { api } from 'constants/constant';
import { getHeaders } from 'auth/auth';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export const adminFeesworksheetAPI = createApi({
  reducerPath: 'Feesworksheet',
  tagTypes: ['getFees'],
  baseQuery: fetchBaseQuery({ baseUrl: api, credentials: 'include' }),
  endpoints: (builder) => ({
    //to get fees worksheet data
    getUserFeesWorksheet: builder.query({
      query: ({ application_Id }) => {
        return {
          url: `b2b/loan/fees/${application_Id}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      providesTags: ['getFees'],
    }),
    getFeesWorksheet: builder.query({
      query: ({ applicationId }) => {
        const url = `/b2b/loan/applications/${applicationId}/fees-worksheet`;
        return {
          url,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      providesTags: ['getFees'],
    }),
    getFeesWorksheetTotals: builder.query({
      query: ({ applicationId }) => {
        return {
          url: `/b2b/loan/applications/${applicationId}/fees-worksheet/totals`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      providesTags: ['getFees'],
    }),
    //to post and save fees worksheet data
    saveUserFeesWorksheet: builder.mutation({
      query: ({ data, applicationId }) => {
        return {
          url: `b2b/loan/fees/${applicationId}`,
          method: 'POST',
          headers: getHeaders(),
          body: data,
        };
      },
      invalidatesTags: ['getFees'],
    }),
    //to update fees work sheet data
    updateUserFeesWorksheet: builder.mutation({
      query: ({ data, applicationId }) => {
        return {
          url: `b2b/loan/fees/${applicationId}`,
          method: 'PUT',
          headers: getHeaders(),
          body: data,
        };
      },
      invalidatesTags: ['getFees'],
    }),
    // Who can be sent this worksheet, and by which route. The send endpoint
    // applies the same rules; this is so the dialog can say no first.
    getSendRecipients: builder.query({
      query: ({ applicationId, deliveryMethod = 'portal' }) => {
        return {
          url: `/b2b/loan/applications/${applicationId}/fees-worksheet/recipients`,
          method: 'GET',
          headers: getHeaders(),
          params: { delivery_method: deliveryMethod },
        };
      },
    }),
    sendToBorrower: builder.mutation({
      query: ({ applicationId, ...body }) => {
        return {
          url: `/b2b/loan/applications/${applicationId}/fees-worksheet/send-to-borrower`,
          method: 'POST',
          headers: getHeaders(),
          body,
        };
      },
      // The send moves the worksheet's status and sharing state.
      invalidatesTags: ['getFees'],
    }),
    revokeWorksheetAccess: builder.mutation({
      query: ({ applicationId }) => {
        return {
          url: `/b2b/loan/applications/${applicationId}/fees-worksheet/revoke`,
          method: 'POST',
          headers: getHeaders(),
        };
      },
      // Same tag as the send: both move the sharing state the toolbar reads,
      // and the chip would otherwise keep saying "Shared" after a revocation.
      invalidatesTags: ['getFees'],
    }),
    // Everything the screen changed, in one request. The per-line mutations
    // below remain for callers that have not moved over yet.
    saveFeesWorksheet: builder.mutation({
      query: ({ applicationId, ...body }) => {
        return {
          url: `/b2b/loan/applications/${applicationId}/fees-worksheet/line-items`,
          method: 'PUT',
          headers: getHeaders(),
          body,
        };
      },
      invalidatesTags: ['getFees'],
    }),
    addManualFee: builder.mutation({
      query: ({ applicationId, ...body }) => {
        return {
          url: `/b2b/loan/applications/${applicationId}/fees-worksheet/line-items`,
          method: 'POST',
          headers: getHeaders(),
          body,
        };
      },
      invalidatesTags: ['getFees'],
    }),
    updateLineItem: builder.mutation({
      query: ({ applicationId, itemId, ...body }) => {
        return {
          url: `/b2b/loan/applications/${applicationId}/fees-worksheet/line-items/${itemId}`,
          method: 'PATCH',
          headers: getHeaders(),
          body,
        };
      },
      invalidatesTags: ['getFees'],
    }),
    deleteLineItem: builder.mutation({
      query: ({ applicationId, itemId }) => {
        return {
          url: `/b2b/loan/applications/${applicationId}/fees-worksheet/line-items/${itemId}`,
          method: 'DELETE',
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['getFees'],
    }),
    getLoanCostTypes: builder.query({
      query: () => {
        return {
          url: `b2b/loan/fees/loan_cost_types`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),
    getLoanAndRate: builder.query({
      query: ({ applicationId }) => {
        return {
          url: `b2b/loans/product/rate_and_loan_amount/application/${applicationId}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),

    getSelectedPricingQuote: builder.query({
      query: ({ applicationId }) => {
        return {
          url: `/b2b/loan/applications/${applicationId}/fees-worksheet/pricing/selected-quote`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      providesTags: ['getFees'],
    }),
    getLoadstarData: builder.query({
      query: ({ applicationId }) => {
        return {
          url: `b2b/loan/fees/closing_cost_calculation/${applicationId}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),
    // get lender
    getRateVendors: builder.query({
      query: () => {
        return {
          url: `b2b/loan/product/rate_vendors`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),

    updateFeesFromSettleWise: builder.mutation({
      query: ({ application_id }) => {
        return {
          url: `b2b/loan/fees/update_fees_from_settlewise/${application_id}`,
          method: 'PUT',
          headers: getHeaders(),
        };
      },
    }),

    // New Backend V2 Autofill Endpoints
    autofillFromPricing: builder.mutation({
      query: ({ applicationId, data, pricing_request_id, quote_id }) => {
        return {
          url: `/b2b/loan/applications/${applicationId}/fees-worksheet/autofill/pricing`,
          method: 'POST',
          headers: getHeaders(),
          body: {
            fee_summary: data,
            pricing_request_id,
            quote_id,
            lender_name: data.lender_name,
          },
        };
      },
      invalidatesTags: ['getFees'],
    }),
    syncPricing: builder.mutation({
      query: ({ applicationId, pricing_request_id, quote_id }) => {
        return {
          url: `/b2b/loan/applications/${applicationId}/fees-worksheet/sync-pricing`,
          method: 'POST',
          headers: getHeaders(),
          body: {
            pricing_request_id,
            quote_id,
          },
        };
      },
      invalidatesTags: ['getFees'],
    }),

    autofillFromLodestar: builder.mutation({
      query: ({ applicationId, quote, pricing_request_id, quote_id }) => {
        const normalizedQuote = quote?.quote ?? quote;
        return {
          url: `/b2b/loan/applications/${applicationId}/fees-worksheet/autofill/lodestar`,
          method: 'POST',
          headers: getHeaders(),
          body: {
            quote: normalizedQuote,
            pricing_request_id,
            quote_id,
          },
        };
      },
      invalidatesTags: ['getFees'],
    }),

    autofillPrepaids: builder.mutation({
      query: ({ applicationId, data, pricing_request_id, quote_id }) => {
        return {
          url: `/b2b/loan/applications/${applicationId}/fees-worksheet/autofill/prepaids`,
          method: 'POST',
          headers: getHeaders(),
          body: {
            ...data,
            pricing_request_id,
            quote_id,
          },
        };
      },
      invalidatesTags: ['getFees'],
    }),

    runLodestarClosingCosts: builder.mutation({
      query: ({ applicationId, data }) => {
        return {
          url: `/b2b/integrations/lodestar/closing-costs`,
          method: 'POST',
          headers: getHeaders(),
          body: {
            loan_application_id: applicationId,
            ...data,
          },
        };
      },
    }),

    refreshAllFees: builder.mutation({
      query: ({ applicationId, pricing_request_id, quote_id, prepaids }) => {
        return {
          url: `/b2b/loan/applications/${applicationId}/fees-worksheet/refresh-all`,
          method: 'POST',
          headers: getHeaders(),
          body: {
            pricing_request_id,
            quote_id,
            prepaids,
          },
        };
      },
      invalidatesTags: ['getFees'],
    }),

    getComplianceCheck: builder.query({
      query: ({ applicationId }) => ({
        url: `/b2b/loan/applications/${applicationId}/fees-worksheet/compliance`,
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: (response) => response?.data || null,
    }),

  }),
});

export const {
  useGetUserFeesWorksheetQuery,
  useGetFeesWorksheetQuery,
  useGetFeesWorksheetTotalsQuery,
  useSaveUserFeesWorksheetMutation,
  useUpdateUserFeesWorksheetMutation,
  useGetLoanCostTypesQuery,
  useGetLoanAndRateQuery,
  useGetSelectedPricingQuoteQuery,
  useGetLoadstarDataQuery,
  useGetRateVendorsQuery,
  useUpdateFeesFromSettleWiseMutation,
  useAutofillFromPricingMutation,
  useSyncPricingMutation,
  useAutofillFromLodestarMutation,
  useAutofillPrepaidsMutation,
  useSaveFeesWorksheetMutation,
  useGetSendRecipientsQuery,
  useSendToBorrowerMutation,
  useRevokeWorksheetAccessMutation,
  useAddManualFeeMutation,
  useUpdateLineItemMutation,
  useDeleteLineItemMutation,
  useRunLodestarClosingCostsMutation,
  useRefreshAllFeesMutation,
  useLazyGetComplianceCheckQuery,
} = adminFeesworksheetAPI;
